/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import type { ConnectionManager, StreamEvent } from './ConnectionManager';
import { MAX_STREAM_SERIALS, METER_LATEST_EVENT, METER_READING_EVENT, type TabToWorker, type WorkerPort } from './protocol';

export const PING_INTERVAL_MS = 10_000;
export const PORT_TIMEOUT_MS = 30_000;

export interface HubClock {
  setInterval: (callback: () => void, ms: number) => unknown;
  now: () => number;
}

interface PortState {
  scopeOk: boolean;
  serials: Map<string, number>;
  /** The keys wanted per channel. */
  channels: Map<string, Set<string>>;
  /** The keys per channel last announced to the tab as dropped. */
  dropped: Map<string, string[]>;
  lastSeen: number;
}

/** A serial-less stream opened with the union of the keys the tabs want on it. */
/** An AMI reading as the stream carries it; only the fields the latest-values merge needs. */
interface LatestReading {
  timestamp?: number;
  response?: Record<string, unknown>;
  [field: string]: unknown;
}

export interface HubChannel {
  manager: ConnectionManager;
  /** The key an event belongs to; it reaches only the tabs that want that key. */
  keyOf: (eventType: string, data: unknown) => string | null;
  /** Trims the wanted keys, newest tab's first, to what one stream may hold. */
  limitKeys?: (keys: string[]) => string[];
}

export class SubscriptionHub {
  private readonly ports = new Map<WorkerPort, PortState>();
  /**
   * The last event of each key on each channel. The server sends a key's current value only when a stream
   * opens or the value changes, so a tab joining an already open stream gets it from here.
   */
  private readonly lastChannelEvents = new Map<string, Map<string, StreamEvent>>();
  /**
   * Each streamed meter's latest values: the backend's `meter.latest` with every later reading merged in, so a
   * tab subscribing to a meter that is already on the stream gets them too. Null means the meter has no reading.
   */
  private readonly latestReadings = new Map<string, LatestReading | null>();
  private requestCounter = 0;

  /**
   * @param channels serial-less streams by name (e.g. the instant KPI stream); each opens with the union
   *                 of the keys the tabs want, only while that union is non-empty, and each event reaches
   *                 only the tabs that want its key.
   */
  constructor(
    private readonly manager: ConnectionManager,
    private readonly clock: HubClock,
    private readonly channels: Readonly<Record<string, HubChannel>> = {},
  ) {
    manager.onEvent((event) => this.route(event));
    manager.onState((status) => {
      // A stream that isn't open sends every meter's latest values again when it (re)opens.
      if (status !== 'open') this.latestReadings.clear();
      this.broadcast({ type: 'state', status });
    });
    manager.onReconnected(() => this.broadcast({ type: 'reconnected' }));
    manager.onLoginRequired(() => this.broadcast({ type: 'login-required' }));
    Object.entries(channels).forEach(([channel, { manager: channelManager }]) => {
      channelManager.onEvent((event) => this.routeChannel(channel, event));
      channelManager.onState((status) => {
        // A stream that isn't open will send fresh values when it (re)opens; never replay older ones.
        if (status !== 'open') this.lastChannelEvents.get(channel)?.clear();
        this.broadcast({ type: 'channel-state', channel, status });
      });
      channelManager.onLoginRequired(() => this.broadcast({ type: 'login-required' }));
    });
    clock.setInterval(() => this.checkLiveness(), PING_INTERVAL_MS);
  }

  addPort(port: WorkerPort): void {
    this.ports.set(port, { scopeOk: false, serials: new Map(), channels: new Map(), dropped: new Map(), lastSeen: this.clock.now() });
    port.onmessage = (event) => this.handle(port, event.data);
  }

  union(): string[] {
    const latest = new Map<string, number>();
    for (const state of this.ports.values()) {
      if (!state.scopeOk) continue;
      state.serials.forEach((requestedAt, serial) => {
        latest.set(serial, Math.max(latest.get(serial) ?? 0, requestedAt));
      });
    }
    return [...latest.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, MAX_STREAM_SERIALS)
      .map(([serial]) => serial);
  }

  private handle(port: WorkerPort, message: TabToWorker): void {
    const state = this.ports.get(port);
    if (!state) return;
    state.lastSeen = this.clock.now();
    switch (message.type) {
      case 'hello':
        state.scopeOk = message.scopeOk;
        if (message.scopeOk) {
          this.allManagers().filter((m) => m.getStatus() === 'stopped').forEach((m) => m.restart());
        }
        port.postMessage({ type: 'state', status: this.manager.getStatus() });
        Object.entries(this.channels).forEach(([channel, { manager: channelManager }]) =>
          port.postMessage({ type: 'channel-state', channel, status: channelManager.getStatus() }),
        );
        this.updateUnion();
        return;
      case 'subscribe': {
        const added = message.serials.map((serial) => serial.toUpperCase()).filter((serial) => !state.serials.has(serial));
        message.serials.forEach((serial) => state.serials.set(serial.toUpperCase(), ++this.requestCounter));
        this.updateUnion();
        if (state.scopeOk) {
          added
            .filter((serial) => this.latestReadings.has(serial))
            .forEach((serial) => port.postMessage({
              type: 'event', eventType: METER_LATEST_EVENT, data: { [serial]: this.latestReadings.get(serial) ?? null },
            }));
        }
        return;
      }
      case 'unsubscribe':
        message.serials.forEach((serial) => state.serials.delete(serial.toUpperCase()));
        this.updateUnion();
        return;
      case 'channel': {
        if (!(message.channel in this.channels)) return;
        const before = state.channels.get(message.channel) ?? new Set<string>();
        if (message.keys.length > 0) state.channels.set(message.channel, new Set(message.keys));
        else state.channels.delete(message.channel);
        this.updateUnion();
        if (state.scopeOk) {
          message.keys
            .filter((key) => !before.has(key))
            .map((key) => this.lastChannelEvents.get(message.channel)?.get(key))
            .forEach((event) => event && port.postMessage({ type: 'event', eventType: event.eventType, data: event.data }));
        }
        return;
      }
      case 'pong':
        return;
      case 'logout':
        this.allManagers().forEach((m) => m.stop());
        this.ports.forEach((portState) => {
          portState.serials.clear();
          portState.channels.clear();
        });
        return;
      case 'bye':
        this.ports.delete(port);
        this.updateUnion();
        return;
    }
  }

  private allManagers(): ConnectionManager[] {
    return [this.manager, ...Object.values(this.channels).map((channel) => channel.manager)];
  }

  private updateUnion(): void {
    const union = this.union();
    [...this.latestReadings.keys()].filter((serial) => !union.includes(serial)).forEach((serial) => this.latestReadings.delete(serial));
    this.manager.setSerials(union);
    Object.entries(this.channels).forEach(([channel, { manager: channelManager, limitKeys = (keys: string[]) => keys }]) => {
      const keys = new Set<string>();
      [...this.ports.values()].reverse().forEach((state) => {
        if (state.scopeOk) state.channels.get(channel)?.forEach((key) => keys.add(key));
      });
      const held = limitKeys([...keys]);
      channelManager.setSerials([...held].sort());
      this.announceDropped(channel, new Set(held));
    });
  }

  /** Tells each tab which of its keys on the channel the stream is not holding, whenever that changes. */
  private announceDropped(channel: string, held: ReadonlySet<string>): void {
    this.ports.forEach((state, port) => {
      if (!state.scopeOk) return;
      const dropped = [...(state.channels.get(channel) ?? [])].filter((key) => !held.has(key)).sort();
      if (dropped.join(',') === (state.dropped.get(channel) ?? []).join(',')) return;
      state.dropped.set(channel, dropped);
      port.postMessage({ type: 'channel-dropped', channel, keys: dropped });
    });
  }

  private routeChannel(channel: string, event: StreamEvent): void {
    const key = this.channels[channel].keyOf(event.eventType, event.data);
    if (key === null) return;
    const lastEvents = this.lastChannelEvents.get(channel) ?? new Map<string, StreamEvent>();
    lastEvents.set(key, event);
    this.lastChannelEvents.set(channel, lastEvents);
    this.ports.forEach((state, port) => {
      if (state.scopeOk && state.channels.get(channel)?.has(key)) {
        port.postMessage({ type: 'event', eventType: event.eventType, data: event.data });
      }
    });
  }

  private route(event: StreamEvent): void {
    const meterEvent = event.eventType === METER_READING_EVENT || event.eventType === METER_LATEST_EVENT;
    const data = meterEvent && event.data && typeof event.data === 'object' ? (event.data as Record<string, unknown>) : null;
    const key = data ? Object.keys(data)[0] : undefined;
    const serial = key?.toUpperCase();
    if (data && key !== undefined && serial !== undefined) this.rememberLatest(event.eventType, serial, data[key]);
    this.ports.forEach((state, port) => {
      if (state.scopeOk && (serial === undefined || state.serials.has(serial))) {
        port.postMessage({ type: 'event', eventType: event.eventType, data: event.data });
      }
    });
  }

  /** Keeps a meter's latest values: replaced by `meter.latest`, and each reading's registers merged in. */
  private rememberLatest(eventType: string, serial: string, data: unknown): void {
    const reading = data && typeof data === 'object' ? (data as LatestReading) : null;
    if (eventType === METER_LATEST_EVENT) {
      this.latestReadings.set(serial, reading);
      return;
    }
    if (!reading || !this.latestReadings.has(serial)) return;
    const held = this.latestReadings.get(serial) ?? null;
    if (!held) {
      this.latestReadings.set(serial, reading);
      return;
    }
    // A null register was not reported; it never replaces a value.
    const reported = Object.fromEntries(Object.entries(reading.response ?? {}).filter(([, value]) => value !== null && value !== undefined));
    const newer = (reading.timestamp ?? 0) >= (held.timestamp ?? 0);
    const response = newer ? { ...held.response, ...reported } : { ...reported, ...held.response };
    this.latestReadings.set(serial, { ...held, timestamp: newer ? reading.timestamp : held.timestamp, response });
  }

  private checkLiveness(): void {
    const now = this.clock.now();
    let dropped = false;
    this.ports.forEach((state, port) => {
      if (now - state.lastSeen > PORT_TIMEOUT_MS) {
        this.ports.delete(port);
        dropped = true;
      } else {
        port.postMessage({ type: 'ping' });
      }
    });
    if (dropped) {
      this.updateUnion();
    }
  }

  private broadcast(message: Parameters<WorkerPort['postMessage']>[0]): void {
    this.ports.forEach((_state, port) => port.postMessage(message));
  }
}
